import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  type CrisisProtocol,
  crisisProtocol,
  escalateToSupervisor,
  type SupervisionMessage,
  sendSupervisionMessage,
  supervisionThread,
} from '../api.ts';
import { apiMessage } from '../messages.ts';
import { Announcer } from './Announcer.tsx';
import { CrisisProtocolCard } from './CrisisProtocolCard.tsx';

/**
 * "Allerta supervisore", dal lato di chi è in conversazione (sezione 5.9).
 *
 * Due cose che questa schermata non fa, e che sono il vincolo 4:
 *
 *  - **non fa entrare nessuno nella conversazione.** Il supervisore non è
 *    partecipante della room, non riceve alcun flusso e non vede il contenuto:
 *    qui non si conia nessun token e non si nomina nessuna room;
 *  - **non promette una risposta che non arriverà.** Se nessuno è reperibile —
 *    che in un ente piccolo di sera è il caso normale — il thread resta scritto
 *    per la supervisione, e quello che compare subito è il protocollo dell'ente
 *    con il numero del reperibile telefonico. "Il volontario non deve mai
 *    vedere solo «nessuno disponibile»: deve vedere cosa fare adesso."
 */

const THREAD_POLL_MS = 3000;

type Props = {
  sessionId: string;
  onClose: () => void;
};

export function SupervisionPanel({ sessionId, onClose }: Props) {
  const { t } = useTranslation();

  const [reason, setReason] = useState('');
  const [opened, setOpened] = useState(false);
  const [supervisor, setSupervisor] = useState<{ displayName: string } | null>(null);
  const [protocol, setProtocol] = useState<CrisisProtocol | null>(null);
  const [messages, setMessages] = useState<SupervisionMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  /** Ciò che va detto a chi non guarda lo schermo, in una regione persistente. */
  const [announcement, setAnnouncement] = useState({ text: '', seq: 0 });
  const headingRef = useRef<HTMLHeadingElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  const say = (text: string): void =>
    setAnnouncement((previous) => ({ text, seq: previous.seq + 1 }));

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  /**
   * Il protocollo si carica **da subito**, non solo quando l'allerta riesce.
   *
   * Se la richiesta fallisce — rete, server, sessione appena chiusa — l'unica
   * cosa a schermo sarebbe stata "riprova", con una persona in linea e una
   * crisi in corso: un vicolo cieco nel punto in cui la sezione 5.9 chiede il
   * contrario. Il protocollo non dipende dall'escalation e non deve dipenderne.
   */
  useEffect(() => {
    void crisisProtocol()
      .then((state) => setProtocol(state.protocol))
      .catch(() => undefined);
  }, []);

  /**
   * Il thread già aperto si riprende, non si ricomincia.
   *
   * Chiudere il pannello per tornare alla conversazione e riaprirlo è normale;
   * dover riscrivere un motivo per rileggere quello che il supervisore ha
   * risposto, no.
   */
  useEffect(() => {
    void supervisionThread(sessionId)
      .then((result) => {
        setMessages(result.messages);
        setProtocol(result.protocol);
        /*
          Il motivo lo dice il **server**, non la casella di testo di prima:
          riaprendo il pannello su un thread già aperto quella casella è vuota,
          e chi rientra deve rileggere quello che aveva scritto.
        */
        setReason(result.reason);
        setSupervisor(result.claimed && result.volunteer ? { displayName: '' } : null);
        setOpened(true);
      })
      .catch(() => undefined);
  }, [sessionId]);

  /**
   * Il thread si rilegge da solo finché è aperto.
   *
   * Interrogazione periodica come tutto il resto della console (O4): una
   * connessione persistente in più su un VPS singolo andrebbe giustificata, e
   * tre secondi di ritardo su un messaggio scritto non cambiano niente.
   */
  const poll = useCallback(async () => {
    try {
      const result = await supervisionThread(sessionId);
      setMessages(result.messages);
      setProtocol(result.protocol);
      setReason(result.reason);
      /**
       * Se qualcuno ha preso in carico il thread **dopo**, la schermata smette
       * di dire "nessuno reperibile": continuare a dirlo mentre arrivano le
       * risposte è la frase falsa nel momento sbagliato.
       */
      if (result.claimed) setSupervisor((current) => current ?? { displayName: '' });
    } catch {
      // Un giro perso non è il canale perso: si riprova.
    }
  }, [sessionId]);

  useEffect(() => {
    if (!opened) return;
    void poll();
    const handle = setInterval(() => void poll(), THREAD_POLL_MS);
    return () => clearInterval(handle);
  }, [opened, poll]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `messages` non si legge nel corpo, ma è la ragione per cui l'effetto esiste: senza, i messaggi nuovi finirebbero sotto il bordo del riquadro.
  useEffect(() => {
    const node = threadRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  const open = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    /**
     * Il motivo è facoltativo: si allerta con un tocco.
     *
     * "Il pulsante «Allerta supervisore» è raggiungibile in un tap durante
     * tutta la sessione" (sezione 7bis): pretendere una frase prima vorrebbe
     * dire che il primo tocco non avvisa nessuno, con una persona in linea.
     * Quello che c'è da dire si scrive nel thread, subito dopo.
     */
    const trimmed = reason.trim();
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await escalateToSupervisor(sessionId, trimmed);
      setSupervisor(result.supervisor);
      setProtocol(result.protocol);
      setMessages(result.messages);
      setOpened(true);
      say(
        result.supervisor
          ? t('supervision.online', { name: result.supervisor.displayName })
          : `${t('supervision.none_title')}. ${t('supervision.none_body')}`,
      );
    } catch (caught) {
      const key = caught instanceof ApiError ? caught.messageKey : 'errors.unexpected';
      /**
       * Lo **stesso** testo alla regione e al banner.
       *
       * La regione diceva "non è riuscito" e il banner diceva anche *perché*:
       * finché il banner aveva `role="alert"` erano rotti tutti e due, ma da
       * quando il ruolo è tolto — giustamente, nasceva già pieno — il perché
       * sarebbe rimasto **solo visivo**, sull'esito più grave del pannello.
       * Il colore è la seconda copia dell'informazione, mai la sola
       * (sezione 7bis): vale anche per il riquadro che lo porta.
       */
      const message = `${t('supervision.failed')} ${apiMessage(t, key)}`;
      setError(message);
      say(message);
    } finally {
      setBusy(false);
    }
  };

  const send = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    try {
      const result = await sendSupervisionMessage(sessionId, body);
      setMessages((current) => [...current, result.message]);
      setError(undefined);
    } catch {
      // Il messaggio torna nella casella invece di sparire.
      setDraft(body);
      setError(t('supervision.not_sent'));
      /**
       * E si **dice**, non solo si mostra.
       *
       * Il banner qui sotto nasce insieme al proprio testo, quindi non viene
       * annunciato (R10): a chi non vede il messaggio non partito non
       * risultava, e la conversazione con il supervisore proseguiva da una
       * parte sola. Era l'unico esito del pannello che finiva in `setError`
       * senza passare da `say`.
       */
      say(t('supervision.not_sent'));
    }
  };

  return (
    <section className="card" aria-labelledby="supervision-heading">
      <h2 id="supervision-heading" ref={headingRef} tabIndex={-1}>
        {t('supervision.alert_title')}
      </h2>

      {/*
        Regione montata **prima** e vuota: una regione live che nasce già piena
        non viene letta (R10), e qui il messaggio che non verrebbe letto è la
        risposta a "cosa faccio adesso".
      */}
      <Announcer polite={announcement.text} seq={announcement.seq} assertive="" />

      {!opened ? (
        <form onSubmit={(event) => void open(event)}>
          {/*
            La riga che spiega il pannello, e che dice la cosa più importante
            di tutte: il supervisore non sente la conversazione e non ci entra
            mai (vincolo 4). Era `.muted`, cioè il grigio delle note in fondo
            alla pagina.
          */}
          <p className="card__sub">{t('supervision.alert_hint')}</p>
          <div className="field">
            <label htmlFor="escalate-reason">{t('supervision.reason_label')}</label>
            <textarea
              id="escalate-reason"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          {/*
            Il banner è il **secondo** modo di dire la stessa cosa, non il
            primo: la regione live è quella montata vuota qui sopra, e questo
            paragrafo nasce insieme al proprio testo. Con `role="alert"`
            sembrava una regione e non lo era (R10), e dove l'annuncio parte
            davvero sarebbero due letture dello stesso esito (AO12).
          */}
          {error ? <p className="banner banner--danger">{error}</p> : null}
          {/*
            Cambia etichetta, **non** si disabilita: un pulsante disabilitato
            esce dal percorso di tabulazione e scarica il fuoco su `body`
            proprio mentre si aspetta un esito (X3, U2). Il doppio tocco lo
            ferma la guardia dentro il gestore.
          */}
          {/*
            Due uscite di peso diverso: allertare è l'azione, chiudere è il
            ritorno. Erano due pulsanti larghi quanto lo schermo, uno sotto
            l'altro, cioè due uscite che sembravano la stessa — e questa è la
            schermata in cui si preme in fretta.
          */}
          <div className="actions">
            <button type="submit" className="button button--primary button--big">
              {t(busy ? 'supervision.sending' : 'supervision.send_alert')}
            </button>
            <button type="button" className="button button--quiet" onClick={onClose}>
              {t('supervision.close')}
            </button>
          </div>
        </form>
      ) : (
        <>
          {/*
            Chi c'è dall'altra parte, detto com'è davvero.

            Con un reperibile: è stato avvisato. Senza: il messaggio resta e
            verrà letto per la supervisione, non adesso. Etichettarlo come
            assistenza in arrivo farebbe aspettare una risposta che non
            arriverà in tempo (sezione 5.9).
          */}
          {supervisor ? (
            <p className="banner">
              {supervisor.displayName
                ? t('supervision.online', { name: supervisor.displayName })
                : t('supervision.claimed')}
            </p>
          ) : (
            <div className="banner banner--warn">
              <strong>{t('supervision.none_title')}</strong>
              <p>{t('supervision.none_body')}</p>
            </div>
          )}

          {/*
            Il protocollo dell'ente, per primo quando non c'è nessun reperibile:
            è la risposta a "cosa faccio adesso". Con un supervisore avvisato
            resta comunque raggiungibile, sotto la conversazione.
          */}
          {!supervisor ? <CrisisProtocolCard protocol={protocol} /> : null}

          <div
            ref={threadRef}
            className="chat__thread"
            role="log"
            // biome-ignore lint/a11y/noNoninteractiveTabindex: un riquadro che scorre deve essere raggiungibile da tastiera (WCAG 2.1.1, `scrollable-region-focusable`).
            tabIndex={0}
            aria-label={t('supervision.thread_label')}
          >
            {/*
              Il motivo dell'allerta è il **primo messaggio del thread**.

              Era salvato — sta in `session_supervision.reason`, e il
              supervisore lo legge — ma non compariva da nessuna parte a chi
              l'aveva scritto: si allertava scrivendo una frase, il pannello si
              apriva su una conversazione vuota, e i messaggi scritti dopo
              comparivano. Da lì "il primo non viene salvato", che è la cosa
              peggiore da pensare mentre si chiede aiuto.

              Non è una copia locale di quello che si è appena scritto: arriva
              dal server come tutti gli altri, quindi riaprendo il pannello è
              ancora lì. E ha la stessa forma degli altri, con il suo autore
              detto a chi non guarda lo schermo.
            */}
            {reason ? (
              <p className="chat__msg chat__msg--me">
                <span className="visually-hidden">{`${t('supervision.from_volunteer')}: `}</span>
                {reason}
              </p>
            ) : null}
            {messages.map((message) => (
              <p
                key={message.id}
                className={`chat__msg chat__msg--${message.sender === 'volunteer' ? 'me' : 'them'}`}
              >
                <span className="visually-hidden">{`${t(
                  message.sender === 'volunteer'
                    ? 'supervision.from_volunteer'
                    : 'supervision.from_supervisor',
                )}: `}</span>
                {message.body}
              </p>
            ))}
          </div>

          <form className="chat__composer" onSubmit={(event) => void send(event)}>
            <label className="visually-hidden" htmlFor="supervision-draft">
              {t('supervision.write_label')}
            </label>
            <textarea
              id="supervision-draft"
              rows={2}
              maxLength={2000}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('supervision.write_label')}
            />
            <button type="submit" className="button button--primary">
              {t('supervision.send')}
            </button>
          </form>

          {/* Come sopra: il testo lo annuncia la regione, questo lo mostra. */}
          {error ? <p className="banner banner--danger">{error}</p> : null}

          <p className="muted">{t('supervision.privacy_note')}</p>

          {supervisor ? <CrisisProtocolCard protocol={protocol} /> : null}

          {/* Il ritorno alla conversazione: di servizio, non un'azione. */}
          <div className="actions">
            <button type="button" className="button button--quiet" onClick={onClose}>
              {t('supervision.close')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
